import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { JobPostHeader, ScrollableWrapper } from 'view/pages/createJobPost/components';
import { BackButton, Button, Input, Spinner } from 'view/components';
import IndustryDropdownAutocomplete from 'view/pages/profile/components/IndustryDropdownAutocomplete';
import JobCategoryDropdownAutocomplete from 'view/pages/profile/components/JobCategoryDropdownAutocomplete';
import TestMultiplySelect from 'view/pages/profile/TestPage/MultiplySelect';
import JobLocationAutocomplete from 'view/pages/profile/components/JobLocationAutocomplete';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import * as WPS from 'view/pages/profile/WorkPreferences/styled';
import * as CS from 'view/pages/createJobPost/commonStyled';
import * as PSCS from 'view/pages/profile/ProfileSettings/styled';
import { ArrowIcon } from 'assets';
import { useUpdateJobPostByStep } from 'view/pages/createJobPost/hooks/useUpdateJobPostByStep';
import { useContextSelector as useCS } from 'use-context-selector';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { CreateJobPostBackToHomeModal } from 'view/modals';
import { Location, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import { steps } from 'view/layouts/CreateJobPostLayout';
import { TUpdate } from 'domain/types';
import Step from '@mui/material/Step';
import { useStateMachine } from 'little-state-machine';
/* import { updateAction } from 'view/pages/createJobPost/helpers/updateAction'; */
import { updateStep1Action } from 'view/pages/createJobPost/helpers/updateStep1Action';
import { useNavigate } from 'react-router';

interface IFormInput {
  industry: null;
  jobCategory: null;
  jobTitle: '';
  positionType: null;
  jobLocation: null;
  onSite: boolean;
  remote: boolean;
  hybrid: boolean;
}

const JobInformationFields = ({
  onDraftSubmit,
  positionTypeData,
  isPositionTypeDataLoading,
  isUpdating,
}) => {
  const location: Location = useLocation();
  const matches = useMediaQuery(theme.breakpoints.down('desktop'));

  const activeStep = steps.findIndex((step) => {
    return location.pathname.includes(step.path);
  });

  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );

  const createJobPostData = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );

  const {
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<IFormInput>();

  const industryValue = watch('industry') ?? null;
  const jobCategoryValue: any = watch('jobCategory') ?? null;
  const jobTitleValue: any = watch('jobTitle') ?? '';
  const positionTypeValue: any = watch('positionType') ?? null;
  const jobLocationValue = watch('jobLocation') ?? null;

  const isDisabled =
    !!Object.keys(errors).length ||
    !industryValue ||
    !jobCategoryValue ||
    !jobTitleValue ||
    !positionTypeValue?.length ||
    isUpdating;

  return (
    <>
      <CS.CreateJobPostHeader style={{ justifyContent: 'space-between' }}>
        {matches ? (
          <CS.CJPBackButtonBox>
            <BackButton
              text="Back to Home"
              color="#663497"
              onClick={() => {
                if (update) update({ isOpenBackToHomeModal: true });
              }}
            />
          </CS.CJPBackButtonBox>
        ) : null}
        <CS.CreateJobPostHeader>
          <Button
            variant="outlined"
            text="Save as Draft"
            onClick={handleSubmit(onDraftSubmit)}
            style={{ padding: '8px 20px', width: 'max-content' }}
            disabled={isDisabled}
          />
        </CS.CreateJobPostHeader>
      </CS.CreateJobPostHeader>
      {matches ? (
        <div>
          <JobPostHeader
            title={`${createJobPostData?.editStatus ? 'Edit' : 'Create'} Job Post`}
            style={{ marginBottom: '24px', width: 'max-content' }}
          />
          <CS.CustomStepper activeStep={activeStep} orientation="horizontal" alternativeLabel>
            {steps.map((step) => {
              return (
                <Step key={step.label}>
                  <CS.StepLabelText />
                </Step>
              );
            })}
          </CS.CustomStepper>
        </div>
      ) : null}
      <ScrollableWrapper>
        <CS.CreateJobPostMainWrapper>
          <JobPostHeader
            title="Job Information"
            subtitle={
              'Give candidates a high-level overview of the role. \n This helps job seekers quickly understand if the role \n aligns with their career goals.'
            }
          />

          <IndustryDropdownAutocomplete
            industryValue={industryValue}
            setValue={setValue}
            label="Industry *"
          />
          <JobCategoryDropdownAutocomplete
            jobCategoryValue={jobCategoryValue}
            setValue={setValue}
            label="Job Category *"
          />
          <Input name="jobTitle" label="Job Title / Role *" marginbottom={16} />
          {/* <Controller
            name="jobTitle"
            control={control}
            render={({ field }) => {
              return (
                <TestSelect
                  {...field}
                  label="Job Title / Role *"
                  options={jobTitleOptions || []}
                  selectedValue={field.value}
                  onChange={(selected) => {
                    return field.onChange(selected);
                  }}
                  disabled={!jobCategoryValue}
                />
              );
            }}
          /> */}
          <TestMultiplySelect
            name="positionType"
            label="Position Type *"
            options={positionTypeData || []}
            control={control}
            disabled={isPositionTypeDataLoading || !positionTypeData}
          />
          <JobLocationAutocomplete
            setValue={setValue}
            jobLocationValue={jobLocationValue}
            marginbottom={16}
          />
          <div>
            <WPS.TextTitle style={{ marginBottom: '11px' }}>Location Type</WPS.TextTitle>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '9px', marginBottom: '16px' }}
            >
              <PSCS.CheckboxWithText>
                <PSCS.CustomCheckbox
                  name="showPhone"
                  checked={watch('onSite')}
                  onChange={(e) => {
                    return setValue('onSite', e.target.checked);
                  }}
                  disabled={false}
                />
                <p
                  style={{
                    color: '#444444',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '21.76px',
                  }}
                >
                  On-site
                </p>
              </PSCS.CheckboxWithText>
              <PSCS.CheckboxWithText>
                <PSCS.CustomCheckbox
                  name="remote"
                  checked={watch('remote')}
                  onChange={(e) => {
                    return setValue('remote', e.target.checked);
                  }}
                  disabled={false}
                />
                <p
                  style={{
                    color: '#444444',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '21.76px',
                  }}
                >
                  Remote
                </p>
              </PSCS.CheckboxWithText>
              <PSCS.CheckboxWithText>
                <PSCS.CustomCheckbox
                  name="hybrid"
                  checked={watch('hybrid')}
                  onChange={(e) => {
                    return setValue('hybrid', e.target.checked);
                  }}
                  disabled={false}
                />
                <p
                  style={{
                    color: '#444444',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '21.76px',
                  }}
                >
                  Hybrid
                </p>
              </PSCS.CheckboxWithText>
            </div>
          </div>

          <CS.CJPSubmitBtnWrapper>
            <Button
              variant="contained"
              type="submit"
              text="Next"
              style={{ margin: '32px 0' }}
              endIcon={
                isUpdating ? (
                  <Spinner
                    disabled={!!Object.keys(errors).length || isUpdating}
                    variant="contained"
                  />
                ) : (
                  <ArrowIcon rotate="right" color="white" />
                )
              }
              disabled={isDisabled}
            />
          </CS.CJPSubmitBtnWrapper>
        </CS.CreateJobPostMainWrapper>
      </ScrollableWrapper>
      <CreateJobPostBackToHomeModal
        onDraftSubmitClick={handleSubmit(onDraftSubmit)}
        endIcon={
          isUpdating ? (
            <Spinner disabled={!!Object.keys(errors).length || isUpdating} variant="contained" />
          ) : null
        }
        disabled={isDisabled}
      />
    </>
  );
};

const JobInformation = () => {
  const navigate = useNavigate();
  const createJobPostData = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );
  const { data: positionTypeData, isLoading: isPositionTypeDataLoading } = useGetDropdownData(
    DropdownsEnum.POSITION_TYPE
  );

  const { isLoading: isUpdating, updateJobPostByStep } =
    useUpdateJobPostByStep('/create-job-post/step2');

  const getDataToSubmit = (formData: IFormInput) => {
    return {
      industry: formData.industry,
      jobCategory: formData.jobCategory,
      jobTitle: formData.jobTitle,
      positionType: formData.positionType,
      location: formData.jobLocation,
      jobLocation: {
        onSite: formData.onSite,
        remote: formData.remote,
        hybrid: formData.hybrid,
      },
      progressStep: 2,
    };
  };

  const { state, actions } = useStateMachine({ actions: { updateStep1Action } });

  const onSubmit = (formData: IFormInput) => {
    const dataToSend = getDataToSubmit(formData);
    actions.updateStep1Action(dataToSend);
    navigate('/create-job-post/step2');
    /* updateJobPostByStep(dataToSend); */
  };

  const onDraftSubmit = (formData: IFormInput) => {
    const dataToSend = getDataToSubmit(formData);
    const dataToSendWithoutEmptyValues = JSON.parse(
      JSON.stringify({ ...state.data, ...dataToSend })
    );
    dataToSendWithoutEmptyValues.immHiringNeed =
      dataToSendWithoutEmptyValues?.immHiringNeed?.name === 'Yes';
    for (const key in dataToSendWithoutEmptyValues) {
      if (dataToSendWithoutEmptyValues[key] === null || dataToSendWithoutEmptyValues[key] === '') {
        delete dataToSendWithoutEmptyValues[key];
      }
    }
    updateJobPostByStep(
      { ...dataToSendWithoutEmptyValues, editStatus: null, postStatus: 'draft' },
      true
    );
  };

  const defaultValues = {
    industry: state.data.industry || createJobPostData?.industry || null,
    jobCategory: state.data.jobCategory || createJobPostData?.jobCategory || null,
    jobTitle: state.data.jobTitle || createJobPostData?.jobTitle || null,
    positionType: state.data.positionType || createJobPostData?.positionType || null,
    jobLocation: state.data.location || createJobPostData?.location || null,
    onSite: state.data.jobLocation.onSite || createJobPostData?.jobLocation?.onSite || false,
    remote: state.data.jobLocation.remote || createJobPostData?.jobLocation?.remote || false,
    hybrid: state.data.jobLocation.hybrid || createJobPostData?.jobLocation?.hybrid || false,
  };

  return (
    <>
      <FormContainer
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSuccess={onSubmit}
      >
        <JobInformationFields
          onDraftSubmit={onDraftSubmit}
          positionTypeData={positionTypeData}
          isPositionTypeDataLoading={isPositionTypeDataLoading}
          isUpdating={isUpdating}
        />
      </FormContainer>
    </>
  );
};

export default JobInformation;
